// Modal "Novo fluxo" / "Renomear fluxo" (mockup "Modal Novo Fluxo A"):
// nome + grade "Começar a partir de" com 4 templates; ao criar, grava os
// nós do template em /flowbuilder/flow e abre o editor.
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { ArrowForward } from "@mui/icons-material";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { FLOW_TEMPLATES, TONES } from "../../pages/FlowBuilderConfig/flowRules";
import { NodeIcon } from "../../pages/FlowBuilderConfig/nodes/AerosNode";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
    tpl: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      background: "transparent",
      borderRadius: 10,
      padding: "10px 12px",
      display: "flex",
      gap: 10,
      alignItems: "center",
      cursor: "pointer",
      textAlign: "left",
      font: "inherit",
      color: theme.palette.text.primary,
      minWidth: 0,
      "&:hover": { borderColor: "rgba(160,68,255,0.5)" },
    },
    tplOn: { borderColor: "rgba(160,68,255,0.5)", background: "rgba(160,68,255,0.08)" },
    tplTitle: { fontSize: 12.5, fontWeight: 600 },
    tplSub: { fontSize: 10.5, color: theme.palette.text.secondary },
  };
});

const FlowBuilderModal = ({ open, onClose, flowId, nameWebhook = "", onSave }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const history = useHistory();
  const [name, setName] = useState("");
  const [template, setTemplate] = useState("blank");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName(flowId ? nameWebhook || "" : "");
      setTemplate("blank");
      setTouched(false);
    }
  }, [open, flowId, nameWebhook]);

  const nameError = touched && !name.trim();

  const handleSave = async () => {
    setTouched(true);
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (flowId) {
        await api.put("/flowbuilder", { name: name.trim(), flowId });
        toast.success(i18n.t("aeros.flow.newFlow.renamed"));
        onSave && onSave(name.trim());
        onClose();
        return;
      }
      const { data } = await api.post("/flowbuilder", { name: name.trim() });
      const tpl = FLOW_TEMPLATES.find((t) => t.id === template);
      if (data?.id && tpl && tpl.id !== "blank") {
        const { nodes, connections } = tpl.build();
        await api.post("/flowbuilder/flow", { idFlow: data.id, nodes, connections });
      }
      toast.success(i18n.t("aeros.flow.newFlow.created"));
      onSave && onSave(name.trim());
      onClose();
      if (data?.id) history.push(`/flowbuilder/${data.id}`);
    } catch (err) {
      if (err?.response?.status === 402) toast.error(i18n.t("aeros.flow.newFlow.exists"));
      else toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AerosDialog
      open={open}
      onClose={onClose}
      width={480}
      title={i18n.t(flowId ? "aeros.flow.newFlow.editTitle" : "aeros.flow.newFlow.title")}
      onSubmit={handleSave}
      loading={saving}
      submitLabel={i18n.t(flowId ? "aeros.flow.save" : "aeros.flow.newFlow.createOpen")}
      submitIcon={flowId ? undefined : <ArrowForward />}
    >
      <div>
        <label className={`${m.fieldLabel} ${!nameError && name ? m.fieldLabelFocus : ""}`} htmlFor="flow-name">
          {i18n.t("aeros.flow.newFlow.name")}
        </label>
        <TextField
          id="flow-name"
          variant="outlined"
          className={m.input}
          value={name}
          autoFocus
          placeholder={i18n.t("aeros.flow.newFlow.namePlaceholder")}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          error={nameError}
          helperText={nameError ? i18n.t("aeros.flow.newFlow.nameRequired") : ""}
        />
      </div>
      {!flowId && (
        <div>
          <label className={m.fieldLabel}>{i18n.t("aeros.flow.newFlow.startFrom")}</label>
          <div className={classes.grid} role="radiogroup">
            {FLOW_TEMPLATES.map((t) => (
              <button
                type="button"
                key={t.id}
                role="radio"
                aria-checked={template === t.id}
                className={`${classes.tpl} ${template === t.id ? classes.tplOn : ""}`}
                onClick={() => setTemplate(t.id)}
              >
                <NodeIcon name={t.icon} size={20} color={TONES[t.tone].color} />
                <div style={{ minWidth: 0 }}>
                  <div className={classes.tplTitle}>{i18n.t(t.titleKey)}</div>
                  <div className={classes.tplSub}>{i18n.t(t.subKey)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </AerosDialog>
  );
};

export default FlowBuilderModal;
